import { useEffect, useMemo, useState } from "react";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";

/**
 * Per-provider negotiated rates, optionally per day type.
 *
 * By default a nursing shift bills at the NDIS cap x the role's charge %. Where
 * you've agreed something specific with a provider, set it here: a flat rate for
 * the pair (applied with the usual day multiplier), or an explicit rate for a
 * single day type (AM / PM / night / Sat / Sun / public holiday), used as-is.
 * Leave a box blank and that day type falls back to the NDIS-cap default.
 *
 * Each row is one company_service_rate record; saving reconciles the draft
 * against what's stored (create / update / delete per day type).
 */

interface Company { id: string; name: string; }
interface Role { id: string; name: string; billing_role: string | null; charge_percent: string | null; }
interface Rate { id: string; company_id: string; job_role_id: string; day_type: string; hourly_rate: string; }

const DAY_TYPES: Array<[string, string]> = [
    ["all", "Flat rate (all days — applied with the day multiplier)"],
    ["weekday_day", "Weekday day"],
    ["weekday_evening", "Weekday evening (PM)"],
    ["weekday_night", "Weekday night"],
    ["saturday", "Saturday"],
    ["sunday", "Sunday"],
    ["public_holiday", "Public holiday"],
];
const DAY_KEYS = DAY_TYPES.map(([k]) => k);
const num = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : NaN; };

export function NegotiatedRatesCard() {
    const companies = useResource<Company[]>("/companies");
    const roles = useResource<Role[]>("/roles");
    const rates = useResource<Rate[]>("/company-service-rates");

    const [companyId, setCompanyId] = useState("");
    const [roleId, setRoleId] = useState("");
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    const existing = useMemo(
        () => (rates.data ?? []).filter((r) => r.company_id === companyId && r.job_role_id === roleId),
        [rates.data, companyId, roleId]
    );
    const existingByDay = useMemo(() => new Map(existing.map((r) => [r.day_type, r])), [existing]);

    // Seed the draft from what's stored whenever the pair (or the data) changes.
    useEffect(() => {
        const seed: Record<string, string> = {};
        for (const r of existing) seed[r.day_type] = String(num(r.hourly_rate));
        setDraft(seed);
        setMsg(null);
    }, [companyId, roleId, rates.data]);

    const role = (roles.data ?? []).find((r) => r.id === roleId);
    const ready = companyId && roleId;

    const save = async () => {
        if (!ready) return;
        setBusy(true); setMsg(null);
        try {
            for (const dt of DAY_KEYS) {
                const raw = (draft[dt] ?? "").trim();
                const val = raw === "" ? null : num(raw);
                const row = existingByDay.get(dt);
                if (val == null || Number.isNaN(val)) {
                    if (row) await api.delete(`/company-service-rates/${row.id}`);
                } else if (row) {
                    if (num(row.hourly_rate) !== val) {
                        await api.put(`/company-service-rates/${row.id}`,
                            { company_id: companyId, job_role_id: roleId, day_type: dt, hourly_rate: val });
                    }
                } else {
                    await api.post("/company-service-rates",
                        { company_id: companyId, job_role_id: roleId, day_type: dt, hourly_rate: val });
                }
            }
            await rates.reload();
            setMsg("Saved.");
        } catch (err) {
            setMsg(err instanceof Error ? err.message : "Could not save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head">
                <div>
                    <h2>Negotiated rates</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Optional per-provider overrides. Blank = bill at the NDIS cap × the role's charge %.
                        Set a flat rate, or an exact rate per day type where you've agreed one.
                    </p>
                </div>
            </div>
            <div className="card-body">
                <div className="form-grid">
                    <div className="field half">
                        <label>Provider (organisation)</label>
                        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                            <option value="">— pick a provider —</option>
                            {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="field half">
                        <label>Role</label>
                        <select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                            <option value="">— pick a role —</option>
                            {(roles.data ?? []).map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name}{r.billing_role ? ` (bills as ${r.billing_role} · ${r.charge_percent ?? 88}%)` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {ready ? (
                    <>
                        <div className="table-wrap" style={{ marginTop: "0.6rem" }}>
                            <table>
                                <thead>
                                    <tr><th>Day type</th><th style={{ width: 180 }}>Negotiated rate ($/hr)</th></tr>
                                </thead>
                                <tbody>
                                    {DAY_TYPES.map(([key, label]) => (
                                        <tr key={key}>
                                            <td>{label}</td>
                                            <td>
                                                <input type="number" min={0} step="0.01"
                                                    value={draft[key] ?? ""}
                                                    placeholder="auto (NDIS cap × %)"
                                                    style={{ width: 150 }}
                                                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.6rem" }}>
                            <button className="btn" disabled={busy} onClick={() => void save()}>
                                {busy ? "Saving…" : "Save negotiated rates"}
                            </button>
                            {role && !role.billing_role && (
                                <span className="help">Heads up: this role isn't mapped to an NDIS item, so with no rate set it won't auto-price.</span>
                            )}
                            {msg && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{msg}</span>}
                        </div>
                    </>
                ) : (
                    <p className="help" style={{ marginTop: "0.5rem" }}>Pick a provider and a role to set their rates.</p>
                )}
            </div>
        </div>
    );
}
